import { getCanonicalSpellSystem, SPELL_SYSTEM_MANA } from './spellSystem';
import {
  SPELL_BINDING_ALL,
  SPELL_BINDING_SURFACE_ANY,
  SPELL_BINDING_SURFACE_SHEET,
  getBoundResourceState,
  getDirectSpellResourceBindings,
} from '../systems/resourceEngine';

function calculateLegacyMaxMana(char = {}) {
  if (Number.isFinite(char?.maxMana) && char.maxMana > 0) {
    return char.maxMana;
  }

  return Object.entries(char?.spellSlots || {}).reduce((total, [level, count]) => {
    const parsedLevel = parseInt(level, 10);
    const parsedCount = Number.isFinite(count) ? count : 0;
    return total + ((Number.isFinite(parsedLevel) ? parsedLevel : 0) * parsedCount);
  }, 0);
}

function getLegacySlotTiers(char = {}) {
  const spellSlots = char?.spellSlots || {};
  const usedSpellSlots = char?.usedSpellSlots || {};
  return Object.keys(spellSlots)
    .map((level) => {
      const parsedLevel = parseInt(level, 10);
      const max = spellSlots?.[level] || 0;
      return {
        level: parsedLevel,
        current: Math.max(0, max - (usedSpellSlots?.[level] || 0)),
        max,
      };
    })
    .filter((tier) => Number.isFinite(tier.level) && tier.max > 0)
    .sort((left, right) => left.level - right.level);
}

function getBindingsForSurface(char, systems, surface) {
  return getDirectSpellResourceBindings(char, systems, surface);
}

function buildSummaryFromState(kind, state, fallbackLabel) {
  if (kind === 'legacy-mana' || kind === 'system-scalar') {
    return {
      displayKind: 'meter',
      label: state?.label || fallbackLabel || 'Mana',
      current: state?.current ?? 0,
      max: state?.max ?? null,
      tiers: [],
      detailText: null,
      canManualAdjust: kind === 'legacy-mana',
    };
  }

  if (kind === 'legacy-slots' || kind === 'system-tiered') {
    const tiers = Array.isArray(state?.tiers) ? state.tiers : [];
    return {
      displayKind: 'tiers',
      label: state?.label || fallbackLabel || 'Spell Slots',
      current: null,
      max: null,
      tiers,
      detailText: tiers.length > 0
        ? tiers.map((tier) => `L${tier.level}: ${tier.current}/${tier.max ?? tier.current}`).join(' | ')
        : null,
      canManualAdjust: false,
    };
  }

  return {
    displayKind: 'mixed',
    label: fallbackLabel || 'Spell Resources',
    current: null,
    max: null,
    tiers: [],
    detailText: null,
    canManualAdjust: false,
  };
}

export function getSpellResourceUiModel({
  char,
  systems = {},
  settings = null,
  surface = SPELL_BINDING_SURFACE_SHEET,
} = {}) {
  const effectiveSettings = settings || char?.settings || {};
  const legacyMode = getCanonicalSpellSystem(effectiveSettings);
  const bindings = getBindingsForSurface(char, systems, surface);
  const anySystemBindings = bindings.length > 0;
  const primaryBindings = bindings.filter((binding) => binding.binding?.assignmentMode === SPELL_BINDING_ALL);
  const primaryStates = primaryBindings
    .map((binding) => ({
      binding,
      state: getBoundResourceState(char, systems, binding),
    }))
    .filter((entry) => entry.state?.available);
  const hasSpecificBindings = bindings.some((binding) => binding.binding?.assignmentMode !== SPELL_BINDING_ALL);

  if (primaryStates.length === 1) {
    const { binding, state } = primaryStates[0];
    return {
      mode: state.kind,
      legacyMode,
      usesSystemsAsPrimary: true,
      usesLegacyManaAsPrimary: false,
      usesLegacySlotsAsPrimary: false,
      hasAnySystemBindings: anySystemBindings,
      hasSpecificBindings,
      binding,
      bindings,
      summary: buildSummaryFromState(state.kind, state, state.label || binding.profile?.visibleLabel || binding.profile?.name),
    };
  }

  if (primaryStates.length > 1) {
    return {
      mode: 'system-mixed',
      legacyMode,
      usesSystemsAsPrimary: true,
      usesLegacyManaAsPrimary: false,
      usesLegacySlotsAsPrimary: false,
      hasAnySystemBindings: anySystemBindings,
      hasSpecificBindings,
      bindings,
      summary: {
        displayKind: 'mixed',
        label: 'Spell Resources',
        current: null,
        max: null,
        tiers: [],
        detailText: `${primaryStates.length} systems-managed spell resources`,
        canManualAdjust: false,
      },
    };
  }

  if (legacyMode === SPELL_SYSTEM_MANA) {
    return {
      mode: 'legacy-mana',
      legacyMode,
      usesSystemsAsPrimary: false,
      usesLegacyManaAsPrimary: true,
      usesLegacySlotsAsPrimary: false,
      hasAnySystemBindings: anySystemBindings,
      hasSpecificBindings,
      bindings,
      summary: buildSummaryFromState('legacy-mana', {
        label: 'MP',
        current: char?.currentMana ?? 0,
        max: calculateLegacyMaxMana(char),
      }, 'MP'),
    };
  }

  return {
    mode: 'legacy-slots',
    legacyMode,
    usesSystemsAsPrimary: false,
    usesLegacyManaAsPrimary: false,
    usesLegacySlotsAsPrimary: true,
    hasAnySystemBindings: anySystemBindings,
    hasSpecificBindings,
    bindings,
    summary: buildSummaryFromState('legacy-slots', {
      label: 'Spell Slots',
      tiers: getLegacySlotTiers(char),
    }, 'Spell Slots'),
  };
}

export function formatSpellResourceReadout(resourceUi) {
  if (!resourceUi) return null;
  if (resourceUi.summary.displayKind === 'meter') {
    if (Number.isFinite(resourceUi.summary.max)) {
      return `${resourceUi.summary.current ?? 0}/${resourceUi.summary.max}`;
    }
    return `${resourceUi.summary.current ?? 0}`;
  }
  return resourceUi.summary.detailText || 'Systems-managed';
}

export {
  SPELL_BINDING_SURFACE_ANY as SPELL_RESOURCE_SURFACE_ANY,
};
